import React, { Fragment, useState, useEffect } from "react";
import {
  Col,
  Card,
  CardHeader,
  CardBody,
  Form,
  FormGroup,
  Label,
  Row,
} from "reactstrap";
import { useForm } from "react-hook-form";
import { useNavigate, useLocation } from "react-router-dom";
import { toast } from "react-toastify";
import { H3, Breadcrumbs, Btn } from "../../../AbstractElements";
import { getDataWithToken, postDataWithToken } from "../../../Utils/restUtils";
import { getModule, updateModule } from "../../../CommonURL";
import Transparentspinner from "../../Common/loader/Transparentspinner";

const ModulesEdit = () => {
  const {
    register,
    handleSubmit,
    setValue,
    formState: { errors },
  } = useForm();
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  const searchParams = new URLSearchParams(location.search);
  const moduleId = searchParams.get("id");

  useEffect(() => {
    if (moduleId) {
      fetchModuleDetails();
    }
  }, [moduleId]);

  const fetchModuleDetails = async () => {
    setLoading(true);
    try {
      const response = await getDataWithToken(`${getModule}?module_id=${moduleId}`);
      const moduleItem =
        response?.data?.data?.[0] ||
        (Array.isArray(response?.data) ? response.data[0] : response?.data) ||
        (Array.isArray(response) ? response[0] : response);

      if (moduleItem) {
        setValue("name", moduleItem.name || "");
        setValue("code", moduleItem.code || "");
        setValue("description", moduleItem.description || "");
      }
    } catch (error) {
      console.error("Error fetching module data:", error);
      toast.error("Failed to load module details");
    } finally {
      setLoading(false);
    }
  };

  const onSubmit = async (data) => {
    setLoading(true);
    const postRequest = {
      module_id: moduleId,
      name: data.name,
      description: data.description,
    };

    try {
      const response = await postDataWithToken(updateModule, postRequest);
      if (response?.status === 200) {
        if (response?.data?.status === true) {
          toast.success(response?.data?.message || "Module updated successfully");
          navigate(`${process.env.PUBLIC_URL}/configuration/modules`);
        } else {
          toast.error(response?.data?.message || "Failed to update module");
        }
      } else {
        toast.error("Something went wrong. Please try again.");
      }
    } catch (error) {
      console.error("Error updating module:", error);
      toast.error("Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    navigate(`${process.env.PUBLIC_URL}/configuration/modules`);
  };

  return (
    <Fragment>
      {loading && <Transparentspinner />}

      <Breadcrumbs
        mainTitle="Edit Module"
        parent="Configuration"
        title="Edit Module"
      />

      <Card>
        <CardHeader className="pb-0">
          <H3>Edit Module</H3>
        </CardHeader>
        <CardBody>
          <Form
            className="needs-validation"
            noValidate
            onSubmit={handleSubmit(onSubmit)}
          >
            <Row className="g-3">
              <Col md="6">
                <FormGroup>
                  <Label className="form-label" for="name">
                    Name <span className="text-danger">*</span>
                  </Label>
                  <input
                    className={`form-control ${errors.name ? "is-invalid" : ""}`}
                    id="name"
                    type="text"
                    placeholder="Enter module name"
                    {...register("name", {
                      required: "Name is required",
                    })}
                  />
                  {errors.name && (
                    <span className="text-danger small">
                      {errors.name.message}
                    </span>
                  )}
                </FormGroup>
              </Col>

              <Col md="6">
                <FormGroup>
                  <Label className="form-label" for="description">
                    Description <span className="text-danger">*</span>
                  </Label>
                  <textarea
                    className={`form-control ${
                      errors.description ? "is-invalid" : ""
                    }`}
                    id="description"
                    rows="4"
                    placeholder="Enter module description"
                    {...register("description", {
                      required: "Description is required",
                    })}
                  />
                  {errors.description && (
                    <span className="text-danger small">
                      {errors.description.message}
                    </span>
                  )}
                </FormGroup>
              </Col>
            </Row>

            <div className="d-flex gap-2 mt-4">
              <Btn
                attrBtn={{
                  color: "primary",
                  type: "submit",
                  disabled: loading,
                }}
              >
                Update Module
              </Btn>
              <Btn
                attrBtn={{
                  color: "secondary",
                  type: "button",
                  disabled: loading,
                  onClick: handleCancel,
                }}
              >
                Cancel
              </Btn>
            </div>
          </Form>
        </CardBody>
      </Card>
    </Fragment>
  );
};

export default ModulesEdit;
